import { authService } from '../../services/authService';
import { fetchWithStatus } from '../../services/connectionStatus';

/**
 * Talking to the scorekeeping API from the public pages.
 *
 * These pages cannot use the shared apiService, for one specific reason: its
 * interceptors treat an unauthenticated response as a session problem and react
 * accordingly. That is right for the rest of the application and wrong here.
 * The machine page, score entry and the club screen are all reachable with no
 * account at all, and a TV in the corner of the club must never decide it has
 * been logged out and do something about it.
 *
 * What it does share is the base URL, which matters more than it sounds. The
 * application is configured to call the backend directly rather than through the
 * React dev server's proxy, and a relative path quietly goes the other way.
 * That path works until the first write, at which point the proxy rewrites the
 * Origin header and the request is refused.
 */

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');

export function scorekeepingUrl(path) {
  return `${API_BASE}/scorekeeping${path}`;
}

/** The auth header when somebody is signed in, and nothing when they are not. */
function authHeaders() {
  const token = authService.getToken?.();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * A GET that returns parsed JSON, or throws with the server's own message.
 * Never redirects, never logs anybody out.
 */
export async function getJson(path) {
  const response = await fetchWithStatus(scorekeepingUrl(path), { headers: authHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // A 502-504 is the proxy saying the server is down, not the API's answer.
    const unreachable = [502, 503, 504].includes(response.status);
    const error = new Error(
      body.error || (unreachable ? "Can't reach the club server right now." : `HTTP ${response.status}`)
    );
    error.status = response.status;
    error.code = body.code;
    throw error;
  }
  return response.json();
}

/**
 * A POST. Accepts FormData for the photo upload, in which case the content type
 * is deliberately left to the browser so it can set the multipart boundary.
 */
export async function postJson(path, body) {
  const isForm = body instanceof FormData;
  const response = await fetchWithStatus(scorekeepingUrl(path), {
    method: 'POST',
    headers: {
      ...authHeaders(),
      ...(isForm ? {} : { 'Content-Type': 'application/json' })
    },
    body: isForm ? body : JSON.stringify(body)
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || (data.errors && data.errors[0]?.msg) || `HTTP ${response.status}`);
    error.status = response.status;
    error.code = data.code;
    throw error;
  }
  return data;
}

export async function del(path) {
  const response = await fetchWithStatus(scorekeepingUrl(path), {
    method: 'DELETE',
    headers: authHeaders()
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body.error || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return response.json().catch(() => ({}));
}
